import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Download, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Clock, 
  TrendingUp,
  MessageSquare
} from 'lucide-react';
import SuggestionService from './suggestionService';
import { getConfig, isFeatureEnabled, getStatusColor, getPriorityColor } from './config';
import './CustomerSuggestions.css';

const CustomerSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    suggestionType: '',
    priority: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    count: 0,
    totalRecords: 0
  });
  const [statistics, setStatistics] = useState({});
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy] = useState(getConfig('sorting.defaultSortBy', 'createdAt'));
  const [sortOrder] = useState(getConfig('sorting.defaultSortOrder', 'desc'));

  // Fetch suggestions data
  const fetchSuggestions = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page: page.toString(),
        limit: getConfig('pagination.defaultPageSize', 10).toString(),
        sortBy,
        sortOrder,
        ...filters
      };

      const data = await SuggestionService.getSuggestions(params);

      if (data.success) {
        setSuggestions(data.data.suggestions);
        setPagination(data.data.pagination);
        setStatistics(data.data.statistics);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      const data = await SuggestionService.getDashboardStats();
      
      if (data.success) {
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  useEffect(() => {
    fetchSuggestions();
    fetchDashboardStats();
  }, [filters, sortBy, sortOrder]);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Handle suggestion status update
  const updateSuggestionStatus = async (id, status, adminNotes = '') => {
    try {
      const data = await SuggestionService.updateSuggestion(id, { status, adminNotes });
      
      if (data.success) {
        fetchSuggestions(pagination.current);
        fetchDashboardStats();
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error updating suggestion:', error);
    }
  };

  // View suggestion details
  const viewSuggestionDetails = async (id) => {
    try {
      const data = await SuggestionService.getSuggestionById(id);
      
      if (data.success) {
        setSelectedSuggestion(data.data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching suggestion details:', error);
    }
  };

  // Export data
  const exportData = async (format = 'json') => {
    if (!isFeatureEnabled('enableExport')) {
      console.warn('Export feature is disabled');
      return;
    }

    try {
      if (format === 'csv') {
        const blob = await SuggestionService.exportData('csv', filters);
        SuggestionService.downloadFile(blob, getConfig('export.filename.csv', 'customer-suggestions.csv'));
      } else {
        const data = await SuggestionService.exportData('json', filters);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        SuggestionService.downloadFile(blob, getConfig('export.filename.json', 'customer-suggestions.json'));
      }
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  const getStatusColorClass = (status) => {
    const colors = getStatusColor(status);
    return `${colors.bg} ${colors.text}`;
  };

  const getPriorityColorClass = (priority) => {
    const colors = getPriorityColor(priority);
    return `${colors.bg} ${colors.text}`;
  };

  const getStatusIcon = (status) => {
    const icons = {
      'PENDING': <Clock className="w-4 h-4" />,
      'UNDER_REVIEW': <Eye className="w-4 h-4" />,
      'APPROVED': <CheckCircle className="w-4 h-4" />,
      'REJECTED': <XCircle className="w-4 h-4" />,
      'IMPLEMENTED': <CheckCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  return (
    <div className="customer-suggestions">
      {/* Header */}
      <div className="suggestions-header">
        <div className="header-content">
          <h1 className="page-title">
            <MessageSquare className="w-8 h-8" />
            Customer Suggestions
          </h1>
          <p className="page-subtitle">
            Manage and review customer feedback and suggestions
          </p>
        </div>
        
        <div className="header-actions">
          {isFeatureEnabled('enableExport') && (
            <>
              <button 
                onClick={() => exportData('csv')}
                className="btn btn-outline"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              <button 
                onClick={() => exportData('json')}
                className="btn btn-outline"
              >
                <Download className="w-4 h-4" />
                Export JSON
              </button>
            </>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon bg-blue-100 text-blue-600">
            <MessageSquare className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{statistics.overview?.total || 0}</h3>
            <p className="stat-label">Total Suggestions</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-yellow-100 text-yellow-600">
            <Clock className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{statistics.overview?.pending || 0}</h3>
            <p className="stat-label">Pending Review</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-green-100 text-green-600">
            <CheckCircle className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{statistics.overview?.implemented || 0}</h3>
            <p className="stat-label">Implemented</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-purple-100 text-purple-600">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <h3 className="stat-number">{statistics.overview?.implementationRate || 0}%</h3>
            <p className="stat-label">Implementation Rate</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Search</label>
            <div className="search-input">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search suggestions..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="input"
              />
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="select"
            >
              {getConfig('filters.statusOptions', []).map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Type</label>
            <select
              value={filters.suggestionType}
              onChange={(e) => handleFilterChange('suggestionType', e.target.value)}
              className="select"
            >
              {getConfig('filters.typeOptions', []).map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="select"
            >
              {getConfig('filters.priorityOptions', []).map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Suggestions Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading suggestions...</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="suggestions-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>User</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((suggestion) => (
                  <tr key={suggestion._id}>
                    <td>
                      <div className="suggestion-title">
                        <h4>{suggestion.title}</h4>
                        <p className="suggestion-description">
                          {suggestion.description?.substring(0, 100)}...
                        </p>
                      </div>
                    </td>
                    <td>
                      <span className="type-badge">
                        {suggestion.suggestionType}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColorClass(suggestion.status)}`}>
                        {getStatusIcon(suggestion.status)}
                        {suggestion.status}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge ${getPriorityColorClass(suggestion.priority)}`}>
                        {suggestion.priority}
                      </span>
                    </td>
                    <td>
                      <div className="user-info">
                        <div className="user-name">{suggestion.userId?.fullName || 'Anonymous'}</div>
                        <div className="user-email">{suggestion.userId?.email}</div>
                      </div>
                    </td>
                    <td>
                      <div className="date">
                        {new Date(suggestion.createdAt).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => viewSuggestionDetails(suggestion._id)}
                          className="btn-icon btn-view"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > 1 && (
        <div className="pagination">
          <button
            onClick={() => fetchSuggestions(pagination.current - 1)}
            disabled={pagination.current === 1}
            className="btn btn-outline"
          >
            Previous
          </button>
          
          <span className="pagination-info">
            Page {pagination.current} of {pagination.total}
          </span>
          
          <button
            onClick={() => fetchSuggestions(pagination.current + 1)}
            disabled={pagination.current === pagination.total}
            className="btn btn-outline"
          >
            Next
          </button>
        </div>
      )}

      {/* Suggestion Details Modal */}
      {showModal && selectedSuggestion && (
        <SuggestionModal
          suggestion={selectedSuggestion}
          onClose={() => setShowModal(false)}
          onUpdate={updateSuggestionStatus}
        />
      )}
    </div>
  );
};

// Suggestion Details Modal Component
const SuggestionModal = ({ suggestion, onClose, onUpdate }) => {
  const [status, setStatus] = useState(suggestion.status);
  const [priority, setPriority] = useState(suggestion.priority);
  const [adminNotes, setAdminNotes] = useState(suggestion.adminNotes || '');

  const handleUpdate = () => {
    onUpdate(suggestion._id, status, adminNotes);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Suggestion Details</h2>
          <button onClick={onClose} className="modal-close">×</button>
        </div>
        
        <div className="modal-body">
          <div className="suggestion-details">
            <h3>{suggestion.title}</h3>
            <p className="suggestion-description">{suggestion.description}</p>
            
            <div className="suggestion-meta">
              <div className="meta-item">
                <strong>Type:</strong> {suggestion.suggestionType}
              </div>
              <div className="meta-item">
                <strong>Current Status:</strong> {suggestion.status}
              </div>
              <div className="meta-item">
                <strong>Priority:</strong> {suggestion.priority}
              </div>
              <div className="meta-item">
                <strong>Submitted by:</strong> {suggestion.userId?.fullName || 'Anonymous'}
              </div>
              <div className="meta-item">
                <strong>Created:</strong> {new Date(suggestion.createdAt).toLocaleString()}
              </div>
            </div>

            <div className="update-section">
              <h4>Update Suggestion</h4>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="select"
                >
                  <option value="PENDING">Pending</option>
                  <option value="UNDER_REVIEW">Under Review</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="IMPLEMENTED">Implemented</option>
                </select>
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="select"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>

              <div className="form-group">
                <label>Admin Notes</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes about this suggestion..."
                  className="textarea"
                  rows="4"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button onClick={handleUpdate} className="btn btn-primary">
            Update Suggestion
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSuggestions;